import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PageHeader } from "@/pages/layoutChrome";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePersistedClientId } from "@/lib/clientSelection";
import { ANALISIS_FINANCIERO_CONTEXT_LABEL } from "@/modules/analisis_financiero/manifest";
import { obtenerCatalogo, obtenerGiro } from "@/modules/analisis_financiero/analisisFinancieroApi";
import { puedeEscribirAnalisisFinanciero } from "@/modules/analisis_financiero/permisoAnalisisFinanciero";
import { PESTANAS_ANALISIS } from "@/modules/analisis_financiero/leyendas";
import { GIRO_VACIO, giroDesdeApi } from "@/modules/analisis_financiero/giroConstanciaUi";
import GiroConstanciaPanel from "@/modules/analisis_financiero/GiroConstanciaPanel";
import ConfiguracionDatosTab from "@/modules/analisis_financiero/ConfiguracionDatosTab";
import GastosRazonablesTab from "@/modules/analisis_financiero/GastosRazonablesTab";
import ProyectadoCierreTab from "@/modules/analisis_financiero/ProyectadoCierreTab";

const ANIO = new Date().getFullYear();

export default function AnalisisFinancieroPage() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients, { autoPickFirst: true });
  const [ejercicio, setEjercicio] = useState(ANIO);
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [giro, setGiroState] = useState(GIRO_VACIO);
  const [giroVersion, setGiroVersion] = useState(0);
  const [catalogo, setCatalogo] = useState({ rubros: [], meses: [], giros_ejemplo: [] });
  const [tab, setTab] = useState("configuracion");
  const setGiro = (parcial) => {
    setGiroState((prev) => ({ ...prev, ...(parcial || {}) }));
  };
  const puedeEscribir = puedeEscribirAnalisisFinanciero(user);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(Array.isArray(data) ? data : [])).catch(() => setClients([]));
    obtenerCatalogo().then(({ data }) => setCatalogo(data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (!clientId) {
      setGiroState(GIRO_VACIO);
      return;
    }
    let vivo = true;
    obtenerGiro(clientId)
      .then(({ data }) => {
        if (!vivo) return;
        setGiroState(giroDesdeApi(data));
        setGiroVersion((v) => v + 1);
      })
      .catch(() => {
        if (!vivo) return;
        setGiroState(GIRO_VACIO);
        setGiroVersion((v) => v + 1);
      });
    return () => { vivo = false; };
  }, [clientId]);

  const empresa = useMemo(
    () => clients.find((c) => c.id === clientId),
    [clients, clientId],
  );
  const meses = catalogo.meses?.length
    ? catalogo.meses
    : Array.from({ length: 12 }, (_, i) => ({ mes: i + 1, etiqueta: String(i + 1) }));

  return (
    <div className="min-h-full" lang="es-MX" data-testid="analisis-financiero-page">
      <PageHeader
        title={ANALISIS_FINANCIERO_CONTEXT_LABEL}
        subtitle="Configuración, gastos razonables y proyectado al cierre, con el mismo estado de resultados."
        breadcrumb={[ANALISIS_FINANCIERO_CONTEXT_LABEL]}
      />
      <div className="px-8 pt-2 pb-8 max-w-6xl space-y-4">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-sm border border-[#0F2D5C]/25 bg-[#0F2D5C]/[0.04] px-3 py-1.5 text-xs font-semibold text-[#0F2D5C]"
          data-testid="back-to-panel-general"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al Panel General
        </Link>

        {!puedeEscribir ? (
          <p className="rounded-sm border border-slate-300 bg-slate-50 px-3 py-2 text-sm" data-testid="aviso-consulta">
            Perfil Consulta: puede ver y descargar, pero no cargar, guardar, borrar ni generar la proyección.
          </p>
        ) : null}

        <div className="grid gap-3 md:grid-cols-4">
          <label className="text-xs md:col-span-2">Empresa
            <select
              className="mt-1 w-full rounded-sm border px-2 py-1.5 text-sm"
              value={clientId || ""}
              onChange={(e) => setClientId(e.target.value)}
              data-testid="select-empresa"
            >
              <option value="">Seleccione una empresa</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {(c.razon_social || c.nombre || c.rfc || c.id)} {c.rfc ? `· ${c.rfc}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs">Ejercicio
            <input
              type="number"
              className="mt-1 w-full rounded-sm border px-2 py-1.5 text-sm"
              value={ejercicio}
              min={2000}
              max={2100}
              onChange={(e) => setEjercicio(Number(e.target.value))}
              data-testid="select-ejercicio"
            />
          </label>
          <label className="text-xs">Mes de corte
            <select
              className="mt-1 w-full rounded-sm border px-2 py-1.5 text-sm"
              value={mes}
              onChange={(e) => setMes(Number(e.target.value))}
              data-testid="select-mes"
            >
              {meses.map((m) => (
                <option key={m.mes} value={m.mes}>{m.etiqueta}</option>
              ))}
            </select>
          </label>
        </div>
        {empresa ? (
          <p className="text-xs text-slate-500">
            Empresa activa: {empresa.razon_social || empresa.nombre || empresa.rfc}. Un estado de resultados por empresa, ejercicio, mes y rubro.
          </p>
        ) : (
          <p className="text-xs text-amber-900">Dé de alta una empresa en Clientes para guardar el estado de resultados.</p>
        )}

        <GiroConstanciaPanel
          clientId={clientId}
          giro={giro}
          setGiro={setGiro}
          puedeEscribir={puedeEscribir}
          onGuardado={(siguiente) => {
            setGiroState(siguiente);
            setGiroVersion((v) => v + 1);
          }}
        />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm flex flex-wrap h-auto gap-1">
            {PESTANAS_ANALISIS.map((p) => (
              <TabsTrigger key={p.id} value={p.id} className="rounded-sm" data-testid={`tab-trigger-${p.id}`}>
                {p.etiqueta}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="configuracion">
            <ConfiguracionDatosTab
              clientId={clientId}
              ejercicio={ejercicio}
              rubros={catalogo.rubros || []}
              puedeEscribir={puedeEscribir}
            />
          </TabsContent>
          <TabsContent value="gastos">
            <GastosRazonablesTab
              clientId={clientId}
              ejercicio={ejercicio}
              mes={mes}
              giro={giro}
              setGiro={setGiro}
              rubros={catalogo.rubros || []}
              puedeEscribir={puedeEscribir}
              giroVersion={giroVersion}
            />
          </TabsContent>
          <TabsContent value="proyectado">
            <ProyectadoCierreTab
              clientId={clientId}
              ejercicio={ejercicio}
              setEjercicio={setEjercicio}
              mes={mes}
              rubros={catalogo.rubros || []}
              puedeEscribir={puedeEscribir}
              setGiro={setGiro}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
